import * as React from 'react';
import { useTranslation, WithTranslation } from 'react-i18next';
import clsx from 'clsx';

import { PromotionDetails } from 'components/ProductContentsPane/PromotionDetails/PromotionDetails';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { getWeekdayText } from 'lib/util/getWeekdayText';
import { PriceSchedule, ProductShape } from 'lib/util/productShape';
import { printPriceTitle } from 'lib/util/util';
import { LinkableTextArea } from 'components/LinkableTextArea/LinkableTextArea';

export const getScheduleText = (schedule: PriceSchedule, t: WithTranslation['t']) => {
  const weekdayText =
    schedule.weekdays.length >= 7
      ? t('Everyday')
      : schedule.weekdays.map((weekday) => getWeekdayText(weekday, t)).join(',');

  if (schedule.startDate && schedule.endDate) {
    return `${schedule.startDate} ~ ${schedule.endDate} - ${weekdayText}`;
  }
  if (schedule.startDate) {
    return `${schedule.startDate} ~ ${t('Unknown')} - ${weekdayText}`;
  }
  if (schedule.endDate) {
    return `${t('Unknown')} ~ ${schedule.endDate} - ${weekdayText}`;
  }

  return '~';
};

interface OwnProps {
  product: ProductShape;
}

type Props = OwnProps;

export const OperationInfo = ({ product }: Props) => {
  const [selectedPriceScheduleIdx, setSelectedPriceScheduleIdx] = React.useState<number>(0);

  const { t } = useTranslation();

  const currencyFormatter = useCurrencyFormatter();

  return (
    <section className="base-single__section">
      <div className="base-padding-16">
        <p className="newline">
          <LinkableTextArea>{product.description}</LinkableTextArea>
        </p>
        <ul className="c-table1 base-marginTop-32">
          <li className="c-table1__list">
            <p className="c-table1__list__title">{t('Days of Week')}</p>
            <p className="c-table1__list__body">
              {product.operatingWeekdays.map((weekday) => getWeekdayText(weekday, t)).join(',')}
            </p>
          </li>
          {product.durationInHours && (
            <li className="c-table1__list">
              <p className="c-table1__list__title">{t('Duration')}</p>
              <p className="c-table1__list__body">
                {t('{{durationInHours}} hours', {
                  durationInHours: product.durationInHours,
                })}
              </p>
            </li>
          )}
        </ul>
        {(product.priceSchedules ?? []).length > 0 && (
          <>
            <div className="base-marginTop-32">
              <label className="c-schedule__select">
                <select
                  name="priceSchedule"
                  value={selectedPriceScheduleIdx}
                  onChange={(e) => setSelectedPriceScheduleIdx(parseInt(e.target.value))}
                >
                  {product.priceSchedules.map((priceSchedule, idx) => (
                    <option key={idx} value={idx}>
                      {getScheduleText(priceSchedule, t)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <ul className={clsx('c-table2', 'base-margin-top-8')}>
              {product.priceSchedules[selectedPriceScheduleIdx]?.prices.map((price) => (
                <li key={price.unitTitle} className="c-table2__list">
                  <p className="c-table2__list__title">{printPriceTitle(price, t)}</p>
                  <p className="c-table2__list__body">{currencyFormatter.format(price.amount)}</p>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="base-marginTop-16">
          <PromotionDetails product={product} />
        </div>
      </div>
    </section>
  );
};
